import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography,
    Stack, Table, TableHead, TableBody, TableRow, TableCell,
    FormControl, Select, MenuItem, IconButton
} from '@mui/material';
import * as api from './volumeDiscountsApi';
import type { VolumeDiscount, QuantityGroup, VolumeDiscountLimit } from './volumeDiscountsApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    volumeDiscountId: number | null;
}

export function VolumeDiscountView({ volumeDiscountId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<VolumeDiscount>();

    const [quantityGroups, setQuantityGroups] = useState<QuantityGroup[]>([]);
    const [newLimit, setNewLimit] = useState<VolumeDiscountLimit>({ minimumQuantity: 0, discountPercent: 0 });

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const qgs = await api.getQuantityGroups();
            setQuantityGroups(qgs);
            if (volumeDiscountId === null) {
                detail.setItem({ id: 0, name: '', quantityGroup: null, limits: [] });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getVolumeDiscount(volumeDiscountId);
                data.limits = data.limits ?? [];
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [volumeDiscountId]);

    useEffect(() => { load(); }, [load]);

    const item = detail.item;

    const isQuantityGroupValid = !!item?.quantityGroup && !!item.quantityGroup.id;
    // Legacy enforces quantity group only when creating; existing records can save freely.
    const canSave = !detail.isNew || isQuantityGroupValid;

    const cancelEdit = () => {
        detail.cancelEdit();
        setNewLimit({ minimumQuantity: 0, discountPercent: 0 });
    };

    const save = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            const response = detail.isNew
                ? await api.createVolumeDiscount(item)
                : await api.updateVolumeDiscount(item);
            response.limits = response.limits ?? [];
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
            setNewLimit({ minimumQuantity: 0, discountPercent: 0 });
        });
    };

    const updateLimit = (index: number, patch: Partial<VolumeDiscountLimit>) => {
        if (!item) return;
        detail.update('limits', item.limits.map((l, i) => i === index ? { ...l, ...patch } : l));
    };
    const removeLimit = (index: number) => {
        if (!item) return;
        detail.update('limits', item.limits.filter((_, i) => i !== index));
    };
    const addLimit = () => {
        if (!item) return;
        detail.update('limits', [...item.limits, {
            minimumQuantity: Number(newLimit.minimumQuantity),
            discountPercent: Number(newLimit.discountPercent),
        }]);
        setNewLimit({ minimumQuantity: 0, discountPercent: 0 });
    };
    const clearLimits = () => {
        if (!item) return;
        detail.update('limits', []);
        setNewLimit({ minimumQuantity: 0, discountPercent: 0 });
    };

    return (
        <DetailViewShell<VolumeDiscount>
            title={item?.name ?? ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={canSave}
            loading={detail.loading}
            error={detail.error}
            item={item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(vd) => {
                const sortedLimits = detail.isEditing
                    ? vd.limits.map((l, i) => ({ l, i }))
                    : [...vd.limits]
                        .map((l, i) => ({ l, i }))
                        .sort((a, b) => a.l.minimumQuantity - b.l.minimumQuantity);

                return (<>
                    <FormRow label={t('volumeDiscount.name')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{vd.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={vd.name ?? ''}
                                onChange={e => detail.update('name', e.target.value)} />
                        )}
                    </FormRow>

                    <FormRow label={t('volumeDiscount.quantityGroup')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{vd.quantityGroup?.name}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={detail.isNew && !isQuantityGroupValid}>
                                <Select
                                    value={vd.quantityGroup?.id ?? ''}
                                    onChange={e => {
                                        const qg = quantityGroups.find(x => x.id === e.target.value);
                                        detail.update('quantityGroup', qg ?? null);
                                    }}
                                >
                                    {[...quantityGroups]
                                        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                                        .map(qg => (
                                            <MenuItem key={qg.id} value={qg.id}>{qg.name}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...sx13, fontWeight: 'bold', textAlign: 'right' }}>
                                    {t('volumeDiscount.minimumQuantity')}
                                </TableCell>
                                <TableCell sx={{ ...sx13, fontWeight: 'bold', textAlign: 'right' }}>
                                    {t('volumeDiscount.discountPercent')}
                                </TableCell>
                                {detail.isEditing && (
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Button size="small" variant="contained" color="warning"
                                            onClick={clearLimits} disabled={vd.limits.length === 0}>
                                            {t('common.clear')}
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {detail.isEditing && (
                                <TableRow>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <TextField size="small" type="number"
                                            value={newLimit.minimumQuantity}
                                            onChange={e => setNewLimit({ ...newLimit, minimumQuantity: e.target.value === '' ? 0 : Number(e.target.value) })}
                                            inputProps={{ style: { textAlign: 'right' } }} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <TextField size="small" type="number"
                                            value={newLimit.discountPercent}
                                            onChange={e => setNewLimit({ ...newLimit, discountPercent: e.target.value === '' ? 0 : Number(e.target.value) })}
                                            inputProps={{ style: { textAlign: 'right' } }} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Button size="small" variant="contained" color="secondary" onClick={addLimit}>
                                            {t('common.add')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}

                            {sortedLimits.map(({ l, i }) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {!detail.isEditing ? (
                                            <Typography sx={sx13}>{l.minimumQuantity.toFixed(2)}</Typography>
                                        ) : (
                                            <TextField size="small" type="number"
                                                value={l.minimumQuantity}
                                                onChange={e => updateLimit(i, { minimumQuantity: e.target.value === '' ? 0 : Number(e.target.value) })}
                                                inputProps={{ style: { textAlign: 'right' } }} />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {!detail.isEditing ? (
                                            <Typography sx={sx13}>{l.discountPercent.toFixed(2)}</Typography>
                                        ) : (
                                            <TextField size="small" type="number"
                                                value={l.discountPercent}
                                                onChange={e => updateLimit(i, { discountPercent: e.target.value === '' ? 0 : Number(e.target.value) })}
                                                inputProps={{ style: { textAlign: 'right' } }} />
                                        )}
                                    </TableCell>
                                    {detail.isEditing && (
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            <IconButton size="small" color="error" onClick={() => removeLimit(i)}>×</IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>);
            }}
        </DetailViewShell>
    );
}
